<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import {
  getFilteredProductsForAccount,
  VerifyAdmin,
  fetchAccountSingle,
  fetchProducts,
  getParentAccount,
  fetchAccountsProduct,
} from '@/api/accountsAdmin'
import { useRouter, useRoute } from 'vue-router'
import AccountAddProductConfirmModal from '@/components/AccountAddProductConfirmModal.vue'
import { useUserStore } from '@/stores/userStore'

const router = useRouter()
const route = useRoute()
const accountName = ref(route.params.account)
const accountFriendlyName = ref('')
const accountFolderName = ref('')
const newProds = ref()
const productSearch = ref('')
const searchRef = ref()
const allProducts = ref<any[]>([])
const parentList = ref<any[]>([])
const installedProducts = ref<any[]>([])
const expandedProducts = ref<Set<string>>(new Set())
const childrenCache = ref<Record<string, any[]>>({})
const childrenLoading = ref<Record<string, boolean>>({})

// Role-based visibility
const userStore = useUserStore()
const userRole = ref(userStore?.role)

const loadProductsToAdd = async () => {
  try {
    isLoading.value = true
    const data = await getFilteredProductsForAccount(String(accountName.value))
    console.log(data)
    newProds.value = data // Filter active products
  } catch (error) {
    console.error('Failed to load products for account:', error)
  } finally {
    isLoading.value = false
  }
}
const isLoading = ref(true)
const loadAccountFriendlyName = async () => {
  try {
    const data = await fetchAccountSingle(String(accountName.value))
    accountFriendlyName.value = data?.accountNameFriendly || ''
    // IMPORTANT: use canonical folder/juid for routing (aliases can cause downstream API calls to use alias paths)
    accountFolderName.value = data?.juid || data?.folderName || data?.accountName || ''
  } catch (e) {
    console.error('Failed to fetch account friendly name', e)
  }
}

// Table headers
const canSeeProductId = computed(() =>
  ['company_suadmin', 'company_owner'].includes(String(userRole.value))
)
const headers = computed(() => {
  const cols: any[] = [{ title: 'Product Name', value: 'productFriendlyName', sortable: true }]
  if (canSeeProductId.value) {
    cols.push({ title: 'Product ID', value: 'productId', sortable: true, align: 'start' })
  }
  cols.push(
    { title: 'Status', value: 'status', sortable: false, align: 'start' },
    { title: 'Type', value: 'type', sortable: false, align: 'start' },
    { title: 'Actions', value: 'actions', sortable: false, align: 'end', width: '200px' }
  )
  return cols
})

onMounted(loadProductsToAdd)
onMounted(loadAccountFriendlyName)
onMounted(VerifyAdmin)
onMounted(async () => {
  try {
    const [productsRes, parentsRes, installedRes] = await Promise.all([
      fetchProducts(),
      getParentAccount(),
      fetchAccountsProduct(String(accountName.value)),
    ])
    allProducts.value = Array.isArray(productsRes) ? productsRes : []
    parentList.value = parentsRes?.data || []
    installedProducts.value = Array.isArray(installedRes) ? installedRes : []
  } catch (e) {
    console.error('Failed to load product metadata', e)
    allProducts.value = []
    parentList.value = []
    installedProducts.value = []
  }
})

// Confirmation modal state
const showConfirm = ref(false)
const selectedProductPath = ref<string>('')

const openConfirm = (configProduct: any) => {
  const path = String(
    configProduct?.path || configProduct?.product || configProduct?.productName || ''
  )
  if (!path) return
  selectedProductPath.value = path
  showConfirm.value = true
}

// Reload installed products for current account
const loadInstalledProducts = async () => {
  try {
    const installedRes = await fetchAccountsProduct(String(accountName.value))
    installedProducts.value = Array.isArray(installedRes) ? installedRes : []
  } catch (e) {
    console.error('Failed to refresh installed products', e)
    installedProducts.value = []
  }
}

// Auto-expand parents when their children match the search
watch(productSearch, (newQuery) => {
  const q = (newQuery || '').trim().toLowerCase()
  if (!q) {
    expandedProducts.value.clear()
    return
  }

  const list = Array.isArray(newProds.value) ? newProds.value : []

  // Auto-expand all ancestors when a product matches
  list.forEach((p: any) => {
    const fn = String(p?.productFriendlyName || '').toLowerCase()
    const pn = String(p?.productName || p?.product || '').toLowerCase()
    const path = String(p?.path || '').toLowerCase()

    const matches = fn.includes(q) || pn.includes(q) || path.includes(q)

    if (matches) {
      const fullPath = String(p?.fullPath || p?.hierarchy?.fullPath || p?.path || '')

      // Expand all ancestors by walking up the fullPath
      if (fullPath.includes('/')) {
        const parts = fullPath.split('/')
        for (let i = 0; i < parts.length - 1; i++) {
          const ancestorPath = parts.slice(0, i + 1).join('/')
          // Find the ancestor product by fullPath
          const ancestor = list.find((prod: any) => {
            const prodFullPath = String(
              prod?.fullPath || prod?.hierarchy?.fullPath || prod?.path || ''
            )
            return prodFullPath === ancestorPath
          })
          if (ancestor) {
            expandedProducts.value.add(ancestor.path)
          }
        }
      }
    }
  })
})

// Button labeling to clarify disabled reasons
const addButtonLabel = (item: any) => {
  if (isInstalled(item)) return 'Already Added'
  if (isAddDisabled(item)) return 'Requires Parent'
  return 'Add'
}

// Focus search after products load
watch(isLoading, (val, oldVal) => {
  if (oldVal && !val) {
    setTimeout(() => {
      try {
        searchRef.value?.focus?.()
      } catch {}
    }, 0)
  }
})

// Hide inactive products and their children (from newProds, which includes children)
const inactiveProductNames = computed(() => {
  const list = Array.isArray(newProds.value) ? newProds.value : []
  return new Set(
    list
      .filter((p: any) => p?.isActive === false)
      .map((p: any) => String(p?.product || p?.productName || ''))
      .filter(Boolean)
  )
})
const inactivePaths = computed(() => {
  const list = Array.isArray(newProds.value) ? newProds.value : []
  return new Set(
    list
      .filter((p: any) => p?.isActive === false)
      .map((p: any) => String(p?.path || ''))
      .filter(Boolean)
  )
})
const parentPaths = computed(() => new Set((parentList.value || []).map((p: any) => p?.path)))

// Installed product paths for current account
const installedPathSet = computed(() => {
  const set = new Set<string>()
  const list = Array.isArray(installedProducts.value) ? installedProducts.value : []
  for (const p of list) {
    // Add both simple path and fullPath for compatibility
    const simplePath = String(p?.productName || p?.product || p?.path || '')
    const fullPath = String(p?.hierarchy?.fullPath || p?.fullPath || '')

    if (simplePath) set.add(simplePath)
    if (fullPath) set.add(fullPath)
  }
  console.log('installedPathSet:', Array.from(set))
  return set
})

const getParentPath = (path: string) => {
  const idx = path.lastIndexOf('/')
  return idx > 0 ? path.slice(0, idx) : ''
}

const isAddDisabled = (item: any) => {
  const level = Number(item?.level || 0)
  if (level <= 0) return false // Parents are never disabled

  // For children, check if parent is installed
  const fullPath = String(item?.fullPath || item?.hierarchy?.fullPath || item?.path || '')
  if (!fullPath) return false

  const parentPath = getParentPath(fullPath)
  if (!parentPath) return true // Child without parent path should be disabled

  // Try multiple ways to match the parent:
  // 1. Full parent path (e.g., "homenet")
  // 2. Simple parent name (last segment, e.g., "homenet" from "parent/homenet")
  const parentSimpleName = parentPath.split('/').pop() || parentPath

  const isParentInstalled =
    installedPathSet.value.has(parentPath) || installedPathSet.value.has(parentSimpleName)

  console.log(
    `isAddDisabled for ${item?.path}: fullPath=${fullPath}, parentPath=${parentPath}, parentSimpleName=${parentSimpleName}, isParentInstalled=${isParentInstalled}`
  )

  // Child can only be added if parent IS installed
  return !isParentInstalled
}

const isInstalled = (item: any) => {
  // Check the backend-provided flag first
  if (typeof item?.isAlreadyInstalled === 'boolean') {
    return item.isAlreadyInstalled
  }
  // Fallback to local check using fullPath for hierarchy
  const fullPath = String(item?.fullPath || item?.hierarchy?.fullPath || item?.path || '')
  const simplePath = String(item?.path || item?.productName || item?.product || '')
  if (!fullPath && !simplePath) return false

  // Check both fullPath and simple path for compatibility
  return installedPathSet.value.has(fullPath) || installedPathSet.value.has(simplePath)
}

const productStatus = (item: any) => {
  if (typeof item?.isActive === 'boolean') return item.isActive
  const key = String(item?.productName || item?.product || '')
  const path = String(item?.path || '')
  const list = Array.isArray(newProds.value) ? newProds.value : []
  const found = list.find(
    (x: any) => String(x?.product || x?.productName) === key || String(x?.path || '') === path
  )
  return found?.isActive === true
}
const isParent = (item: any) => {
  // Prefer API-provided flags from /product/available
  if (typeof item?.isChild === 'boolean') return !item.isChild
  const typeFromHierarchy = (item as any)?.hierarchy?.productType
  // Treat both 'child' and 'intermediate' as non-parent since they have a parent
  if (typeFromHierarchy === 'child' || typeFromHierarchy === 'intermediate') return false
  if (typeFromHierarchy === 'parent') return true
  const lvl = Number(item?.level)
  if (!Number.isNaN(lvl)) return lvl <= 0
  // Fallback to path depth
  const path = String(item?.path || item?.productName || item?.product || '')
  return !path.includes('/')
}

// Helper to check if product has children in the available list
function hasChildrenInList(product: any, list: any[]): boolean {
  const productFullPath = String(
    product?.fullPath || product?.hierarchy?.fullPath || product?.path || ''
  )
  if (!productFullPath) return false

  return list.some((p: any) => {
    const fullPath = String(p?.fullPath || p?.hierarchy?.fullPath || p?.path || '')
    if (!fullPath || fullPath === productFullPath) return false
    return fullPath.startsWith(productFullPath + '/')
  })
}

// Helper to get direct children of a product
function getDirectChildren(parentFullPath: string, list: any[]): any[] {
  return list.filter((p: any) => {
    const fullPath = String(p?.fullPath || p?.hierarchy?.fullPath || p?.path || '')
    if (!fullPath || fullPath === parentFullPath) return false

    if (fullPath.startsWith(parentFullPath + '/')) {
      const remainder = fullPath.substring(parentFullPath.length + 1)
      return !remainder.includes('/') // Direct child has no more slashes
    }
    return false
  })
}

// Toggle expand/collapse for a product
async function toggleExpand(product: any) {
  const productPath = String(product?.path || '')
  if (!productPath) return

  const isExpanded = expandedProducts.value.has(productPath)

  if (isExpanded) {
    expandedProducts.value.delete(productPath)
  } else {
    expandedProducts.value.add(productPath)
  }
}

// Check if we should show expand icon
function shouldShowExpandIcon(product: any, list: any[]): boolean {
  if (!product || product?.synthetic) return false
  return hasChildrenInList(product, list)
}

const displayItems = computed(() => {
  const list = Array.isArray(newProds.value) ? newProds.value : []
  const q = (productSearch?.value || '').toLowerCase().trim()

  // base filtered list (keep ALL products for hierarchy checking)
  const allFiltered = list.filter((p: any) => {
    const key = String(p?.productName || p?.product || '')
    const path = String(p?.path || '')
    if (!key && !path) return false
    if (inactiveProductNames.value.has(key)) return false
    for (const ip of inactivePaths.value) {
      if (!ip) continue
      if (path === ip) return false
      if (path.startsWith(ip.endsWith('/') ? ip : ip + '/')) return false
    }
    return true
  })

  // Apply search filter
  let filtered = allFiltered
  if (q) {
    // When searching, include products that match OR have children that match
    const matchingPaths = new Set<string>()

    // First pass: find all products that match the search
    allFiltered.forEach((p: any) => {
      const fn = String(p?.productFriendlyName || '').toLowerCase()
      const pn = String(p?.productName || p?.product || '').toLowerCase()
      const path = String(p?.path || '').toLowerCase()
      const fullPath = String(p?.fullPath || p?.hierarchy?.fullPath || p?.path || '')

      if (fn.includes(q) || pn.includes(q) || path.includes(q)) {
        matchingPaths.add(path)

        // Also add all ancestors by walking up the fullPath
        if (fullPath.includes('/')) {
          const parts = fullPath.split('/')
          for (let i = 0; i < parts.length - 1; i++) {
            const ancestorPath = parts.slice(0, i + 1).join('/')
            // Find the ancestor product by fullPath
            const ancestor = allFiltered.find((prod: any) => {
              const prodFullPath = String(
                prod?.fullPath || prod?.hierarchy?.fullPath || prod?.path || ''
              )
              return prodFullPath === ancestorPath
            })
            if (ancestor) {
              matchingPaths.add(ancestor.path)
            }
          }
        }
      }
    })

    // Filter to only matching products and their ancestors
    filtered = allFiltered.filter((p: any) => matchingPaths.has(p.path))
  }

  // Separate parents from filtered list
  // Use fullPath (or hierarchy.fullPath) for hierarchy detection, not path (which is now simple ID)
  const parents = filtered.filter((p: any) => {
    const fullPath = String(p?.fullPath || p?.hierarchy?.fullPath || p?.path || '')
    return !fullPath.includes('/')
  })

  // Filter out parents where all children are already installed
  const parentsToShow = parents.filter((parent: any) => {
    const parentFullPath = String(
      parent?.fullPath || parent?.hierarchy?.fullPath || parent?.path || ''
    )

    // If the parent itself is installed, we might still want to show it if it has uninstalled descendants
    // Get ALL descendants (not just direct children) recursively
    const getAllDescendants = (parentPath: string): any[] => {
      return allFiltered.filter((product: any) => {
        const fullPath = String(
          product?.fullPath || product?.hierarchy?.fullPath || product?.path || ''
        )
        return fullPath !== parentPath && fullPath.startsWith(parentPath + '/')
      })
    }

    const allDescendants = getAllDescendants(parentFullPath)

    // If parent has no descendants, show it if it's not installed
    if (allDescendants.length === 0) {
      return !isInstalled(parent)
    }

    // Show parent if ANY descendant (at any level) is not yet installed
    const hasUninstalledDescendants = allDescendants.some((desc: any) => !isInstalled(desc))
    return hasUninstalledDescendants
  })

  // Build tree structure
  const result: any[] = []

  for (const parent of parentsToShow.sort((a, b) => {
    const pa = String(a?.fullPath || a?.path || '')
    const pb = String(b?.fullPath || b?.path || '')
    return pa.localeCompare(pb)
  })) {
    const parentPath = String(parent?.path || '') // Simple ID for tracking
    const parentFullPath = String(
      parent?.fullPath || parent?.hierarchy?.fullPath || parent?.path || ''
    )
    const isExpanded = expandedProducts.value.has(parentPath)
    const isLoading = childrenLoading.value[parentPath]

    // Check if parent has children in the FULL list (not just filtered)
    const parentHasChildren = hasChildrenInList(parent, allFiltered)

    // Add parent
    result.push({
      ...parent,
      level: 0,
      isParent: true,
      expanded: isExpanded,
      hasChildren: parentHasChildren,
    })

    // Add children if expanded
    if (isExpanded) {
      if (isLoading) {
        result.push({
          path: `${parentPath}__loading`,
          isLoadingRow: true,
          level: 1,
          productFriendlyName: 'Loading children...',
        })
      } else {
        const children = getDirectChildren(parentFullPath, allFiltered)
        for (const child of children.sort((a, b) => {
          const pa = String(a?.fullPath || a?.path || '')
          const pb = String(b?.fullPath || b?.path || '')
          return pa.localeCompare(pb)
        })) {
          const childPath = String(child?.path || '') // Simple ID for tracking
          const childFullPath = String(
            child?.fullPath || child?.hierarchy?.fullPath || child?.path || ''
          )
          const childIsExpanded = expandedProducts.value.has(childPath)
          const childIsLoading = childrenLoading.value[childPath]
          const childHasChildren = hasChildrenInList(child, allFiltered)

          result.push({
            ...child,
            level: 1,
            isParent: childHasChildren,
            expanded: childIsExpanded,
            hasChildren: childHasChildren,
          })

          // Add grandchildren if child is expanded
          if (childIsExpanded) {
            if (childIsLoading) {
              result.push({
                path: `${childPath}__loading`,
                isLoadingRow: true,
                level: 2,
                productFriendlyName: 'Loading children...',
              })
            } else {
              const grandchildren = getDirectChildren(childFullPath, allFiltered)

              for (const grandchild of grandchildren.sort((a, b) => {
                const pa = String(a?.fullPath || a?.path || '')
                const pb = String(b?.fullPath || b?.path || '')
                return pa.localeCompare(pb)
              })) {
                const grandchildPath = String(grandchild?.path || '') // Simple ID for tracking
                const grandchildFullPath = String(
                  grandchild?.fullPath || grandchild?.hierarchy?.fullPath || grandchild?.path || ''
                )
                const grandchildHasChildren =
                  getDirectChildren(grandchildFullPath, allFiltered).length > 0
                const grandchildIsExpanded = expandedProducts.value.has(grandchildPath) // Use simple path for tracking
                const grandchildType = grandchild?.hierarchy?.productType || 'child'

                result.push({
                  ...grandchild,
                  level: 2,
                  isParent: grandchildHasChildren,
                  expanded: grandchildIsExpanded,
                  hasChildren: grandchildHasChildren,
                  productType: grandchildType, // Add productType for consistency
                })

                // Add great-grandchildren if grandchild is expanded
                if (grandchildIsExpanded) {
                  const greatGrandchildren = getDirectChildren(grandchildFullPath, allFiltered)
                  for (const greatGrandchild of greatGrandchildren.sort((a, b) => {
                    const pa = String(a?.fullPath || a?.path || '')
                    const pb = String(b?.fullPath || b?.path || '')
                    return pa.localeCompare(pb)
                  })) {
                    result.push({
                      ...greatGrandchild,
                      level: 3,
                      isParent: false,
                      expanded: false,
                      hasChildren: false,
                    })
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  return result
})

const goBackToAccountProducts = () => {
  const folder = accountFolderName.value || ''
  if (folder) {
    router.push(`/account-admin/${folder}#products`)
  } else {
    router.back()
  }
}

// Row class for styling
const rowClassForItem = (item: any) => {
  const raw = item?.raw ?? item
  if (raw?.isLoadingRow) return 'loading-row-style'
  if (raw?.synthetic) return 'synthetic-parent'
  return ''
}
</script>

<template>
  <v-container>
    <v-row class="align-center">
      <!-- Subheader -->
      <v-col cols="12" class="subheader-sticky">
        <div
          style="
            display: flex !important;
            justify-content: space-between;
            flex-wrap: wrap;
            align-items: center;
          "
        >
          <div class="d-flex align-center" style="gap: 8px">
            <v-btn
              variant="text"
              size="small"
              prepend-icon="mdi-arrow-left"
              class="text-grey-darken-2"
              @click="goBackToAccountProducts"
            >
              Back
            </v-btn>
            <h2 class="text-h5 font-weight-medium" style="margin: 0">Add Product</h2>
          </div>
        </div>
      </v-col>

      <v-col cols="12">
        <p class="text-body-2 text-grey-darken-1" style="margin-top: -6px">
          Select a product to add. You’ll confirm before it’s added to the account.
        </p>
      </v-col>

      <v-col cols="12">
        <!-- Products Table -->
        <v-data-table
          data-testid="add-products-table"
          :headers="headers"
          :items="displayItems"
          :loading="isLoading"
          item-value="name"
          class="elevation-1 sticky-header striped-rows"
          dense
          :items-per-page="50"
          :row-class="rowClassForItem"
          style="max-width: 100%; width: 100%; overflow-x: auto"
        >
          <!-- Search -->
          <template #top>
            <!-- Desktop: title + compact search on the right -->
            <div
              v-if="!$vuetify.display.mdAndDown"
              class="bg-grey-lighten-5 d-flex align-center pb-2"
              style="width: 100%; gap: 8px; padding: 8px 8px 0 8px"
            >
              <div class="text-subtitle-1 font-weight-medium pl-2">Available Products</div>
              <v-spacer></v-spacer>
              <v-text-field
                ref="searchRef"
                v-model="productSearch"
                data-testid="product-search-input"
                placeholder="Search product to add…"
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                density="comfortable"
                variant="outlined"
                color="primary"
                style="max-width: 420px"
              />
            </div>
            <!-- Mobile: title row then full-width search row -->
            <div v-else class="bg-grey-lighten-5 pb-2" style="width: 100%; padding: 8px 8px 0 8px">
              <div class="text-subtitle-1 font-weight-medium pl-2 mb-2">Available Products</div>
              <v-text-field
                ref="searchRef"
                v-model="productSearch"
                data-testid="product-search-input"
                placeholder="Search products…"
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                density="comfortable"
                variant="outlined"
                color="primary"
                style="width: 100%; max-width: 100%"
              />
            </div>
          </template>

          <!-- Product Friendly Name -->
          <template #item.productFriendlyName="{ item }">
            <div
              class="tree-cell"
              :class="{ 'loading-row': item?.isLoadingRow }"
              :style="{
                paddingLeft: `${(item?.level || 0) * 24}px`,
                opacity: item?.synthetic ? 0.6 : item?.isLoadingRow ? 0.7 : 1,
              }"
            >
              <!-- Loading spinner for loading rows -->
              <v-progress-circular
                v-if="item?.isLoadingRow"
                indeterminate
                size="16"
                width="2"
                class="tree-loading-spinner"
              ></v-progress-circular>
              <!-- Expand/collapse icon for items with children -->
              <v-btn
                v-else-if="item?.hasChildren"
                icon
                size="x-small"
                variant="text"
                class="tree-expand-btn"
                :data-testid="`expand-btn-${item?.productName || item?.path}`"
                @click.stop="toggleExpand(item)"
              >
                <v-icon size="small">
                  {{ item?.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                </v-icon>
              </v-btn>
              <!-- Spacer for items without expand icon -->
              <span v-else class="tree-spacer"></span>

              <!-- Product name -->
              <span class="tree-label" :class="{ 'text-grey': item?.isLoadingRow }">
                {{ item?.productFriendlyName || item?.productName || item?.product }}
              </span>
            </div>
          </template>

          <!-- Status -->
          <template #item.status="{ item }">
            <v-chip size="x-small" :color="productStatus(item) ? 'green' : 'red'" variant="tonal">
              {{ productStatus(item) ? 'Active' : 'Disabled' }}
            </v-chip>
          </template>

          <!-- Product ID (visible only for suadmin/owner) -->
          <template v-if="canSeeProductId" #item.productId="{ item }">
            <code>{{ String(item?.path || item?.product || item?.productName || '') }}</code>
          </template>

          <!-- Type: Parent or Child -->
          <template #item.type="{ item }">
            <v-chip size="x-small" :color="isParent(item) ? 'blue' : 'grey'" variant="tonal">
              {{ isParent(item) ? 'Parent' : 'Child' }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template #item.actions="{ item }">
            <div class="d-flex justify-end" style="gap: 6px; width: 100%">
              <template v-if="!item?.synthetic">
                <v-btn
                  color="primary"
                  prepend-icon="mdi-plus"
                  :variant="isInstalled(item) ? 'outlined' : 'flat'"
                  :disabled="isInstalled(item) || isAddDisabled(item)"
                  :data-testid="`${isInstalled(item) ? 'already-added' : isAddDisabled(item) ? 'requires-parent' : 'add'}-btn-${item?.productName || item?.path}`"
                  :aria-label="`${addButtonLabel(item)} ${item?.productFriendlyName || item?.productName || item?.product}`"
                  @click.stop="openConfirm(item)"
                >
                  {{ addButtonLabel(item) }}
                </v-btn>
              </template>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Footer actions removed (Back moved to header) -->

    <!-- Add Product Confirmation Modal -->
    <AccountAddProductConfirmModal
      v-if="showConfirm"
      :show="showConfirm"
      :account-juid="String(accountName)"
      :account-friendly-name="accountFriendlyName || String(accountName)"
      :product-path="selectedProductPath"
      @close="showConfirm = false"
      @success="
        () => {
          showConfirm = false
          loadInstalledProducts()
          loadProductsToAdd()
        }
      "
    />
  </v-container>
</template>
<style scoped>
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.subheader-sticky {
  position: sticky;
  top: 56px;
  z-index: 3;
  background: white;
}

.construction-overlay-card {
  position: absolute;
  top: 50vh;
  left: 50vw;
  padding: 10px;
}

h1 {
  margin-bottom: 20px;
}

.custom-button {
  width: 25%;
  /* 1/4th of the container width */
  min-width: 150px;
  /* Optional: Ensure a reasonable minimum width */
}

.new-account-cta-row {
  padding-top: 15px;
  margin: 0;
}

::deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}

/* Add this to your style section */
::deep(.v-data-table .inactive-row) {
  background-color: rgba(0, 0, 0, 0.774) !important;
}

::deep(.v-data-table .inactive-row > td) {
  color: #999 !important;
  opacity: 0.8;
}

::deep(.v-data-table .inactive-row:hover > td) {
  background-color: rgba(15, 4, 4, 0.887) !important;
}

/* Optional: Add "Inactive" indicator for mobile view */
::deep(.v-data-table .inactive-row .v-data-table__mobile-row:first-child::after) {
  content: ' (Inactive)';
  color: #f44336;
  font-size: 0.8em;
}

::deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

::deep(.striped-rows .v-data-table__tr:nth-child(even)) {
  background-color: #f8f9fa;
  /* Very light gray - adjust to match your theme */
}

/* Hover effect */
::deep(.striped-rows .v-data-table__tr:hover) {
  background-color: #e9ecef !important;
  /* Slightly darker on hover */
}

/* Ensure text remains readable on striped rows */
::deep(.striped-rows .v-data-table__tr) {
  color: inherit;
  /* Inherits from parent */
}

.sticky-actions {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 10px 12px;
  z-index: 3;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

/* Tree table styles */
.tree-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.tree-expand-btn {
  flex-shrink: 0;
  margin-left: -4px;
}

.tree-spacer {
  width: 28px;
  flex-shrink: 0;
}

.tree-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tree-loading-spinner {
  flex-shrink: 0;
  margin-left: 2px;
  margin-right: 4px;
}

/* Loading row styles */
::deep(tr.loading-row-style) {
  cursor: default !important;
  pointer-events: none;
}

::deep(tr.loading-row-style td) {
  font-style: italic;
  color: #999 !important;
}

::deep(tr.loading-row-style:hover) {
  background-color: inherit !important;
}

/* Synthetic parent rows */
::deep(tr.synthetic-parent) {
  background-color: #fafafa !important;
}

::deep(tr.synthetic-parent td) {
  color: #8a8a8a !important;
}

::deep(tr.synthetic-parent:hover) {
  background-color: #f4f4f4 !important;
}
</style>
